import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import AuthLayout from '../../components/AuthLayout';
import GoogleSignInButton from '../../components/GoogleSignInButton';
import { expertAuthApi, getErrorMessage } from '../../lib/http';
import logo from '../../assets/logo.png';

// Phone validation regex (E.164 format, but flexible for user input)
const phoneRegex = /^[+]?[1-9]\d{6,14}$/;

// Validation schema for step 1 (phone)
const phoneSchema = z.object({
  phone: z.string()
    .min(7, 'Phone number must be at least 7 digits')
    .max(16, 'Phone number is too long')
    .refine((val) => phoneRegex.test(val.replace(/[\s\-\(\)]/g, '')), {
      message: 'Please enter a valid phone number',
    }),
});

// Validation schema for step 2 (verification code)
const codeSchema = z.object({
  code: z.string()
    .min(4, 'Code must be at least 4 digits')
    .max(8, 'Code is too long')
    .regex(/^\d+$/, 'Code must contain only digits'),
});

// Validation schema for step 3 (account details)
const accountSchema = z.object({
  displayName: z.string().min(1, 'Display name is required'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(10, 'Password must be at least 10 characters'),
  confirmPassword: z.string(),
  timezone: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type PhoneFormData = z.infer<typeof phoneSchema>;
type CodeFormData = z.infer<typeof codeSchema>;
type AccountFormData = z.infer<typeof accountSchema>;

type Step = 'phone' | 'verify' | 'account' | 'success';

const ExpertSignup: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('phone');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [phone, setPhone] = useState<string>('');
  const [verificationToken, setVerificationToken] = useState<string>('');
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Google OAuth state
  const [showGoogleProfileForm, setShowGoogleProfileForm] = useState(false);
  const [googleAccessToken, setGoogleAccessToken] = useState<string>('');
  const [suggestedName, setSuggestedName] = useState<string>('');
  const [googleDisplayName, setGoogleDisplayName] = useState<string>('');

  // Phone form
  const phoneForm = useForm<PhoneFormData>();

  // Code form
  const codeForm = useForm<CodeFormData>();

  // Account form
  const accountForm = useForm<AccountFormData>({
    defaultValues: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    },
  });

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Step 1: Send verification code
  const onPhoneSubmit = async (data: PhoneFormData) => {
    setError('');
    setIsLoading(true);

    try {
      const normalizedPhone = data.phone.replace(/[\s\-\(\)]/g, '');
      await expertAuthApi.sendPhoneCode(normalizedPhone);
      setPhone(normalizedPhone);
      setStep('verify');
      setResendCooldown(60);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify code
  const onCodeSubmit = async (data: CodeFormData) => {
    setError('');
    setIsLoading(true);

    try {
      const response = await expertAuthApi.verifyPhoneCode(phone, data.code);
      setVerificationToken(response.data.verification_token || '');
      setStep('account');
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Create account
  const onAccountSubmit = async (data: AccountFormData) => {
    setError('');
    setIsLoading(true);

    try {
      await expertAuthApi.signup({
        email: data.email,
        phone: phone,
        phone_verification_token: verificationToken,
        password: data.password,
        password_confirmation: data.confirmPassword,
        display_name: data.displayName,
        timezone: data.timezone || 'UTC',
      });
      setRegisteredEmail(data.email);
      setStep('success');
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Resend code
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setError('');
    setIsLoading(true);

    try {
      await expertAuthApi.sendPhoneCode(phone);
      setResendCooldown(60);
      codeForm.reset();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Google OAuth handlers
  const handleGoogleSuccess = async (accessToken: string) => {
    setError('');
    setIsLoading(true);

    try {
      const response = await expertAuthApi.googleAuth({ access_token: accessToken });
      sessionStorage.setItem('sessionRole', 'expert');
      sessionStorage.setItem('expert', JSON.stringify(response.data.expert));

      // Check if phone verification is needed
      if (response.data.requires_phone) {
        navigate('/expert/complete-profile');
      } else {
        navigate('/app/dashboard');
      }
    } catch (err: any) {
      // Check if we need to collect display_name for new expert
      if (err.response?.data?.requires_profile) {
        setGoogleAccessToken(accessToken);
        setSuggestedName(err.response.data.suggested_name || '');
        setGoogleDisplayName(err.response.data.suggested_name || '');
        setShowGoogleProfileForm(true);
        setIsLoading(false);
        return;
      }
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!googleDisplayName.trim()) {
      setError('Display name is required');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await expertAuthApi.googleAuth({
        access_token: googleAccessToken,
        display_name: googleDisplayName.trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      sessionStorage.setItem('sessionRole', 'expert');
      sessionStorage.setItem('expert', JSON.stringify(response.data.expert));

      // Check if phone verification is needed
      if (response.data.requires_phone) {
        navigate('/expert/complete-profile');
      } else {
        navigate('/app/dashboard');
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign up failed. Please try again.');
  };

  // Google profile form for new users
  if (showGoogleProfileForm) {
    return (
      <AuthLayout role="expert" title="Complete Your Profile">
        <form onSubmit={handleGoogleProfileSubmit} className="space-y-5">
          {error && (
            <div
              role="alert"
              className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm"
              aria-live="polite"
            >
              {error}
            </div>
          )}

          <div className="bg-pink-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-pink-800">
              Just one more step! Enter your display name to complete your expert profile.
            </p>
          </div>

          <div>
            <label
              htmlFor="googleDisplayName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Display Name
            </label>
            <input
              id="googleDisplayName"
              type="text"
              value={googleDisplayName}
              onChange={(e) => setGoogleDisplayName(e.target.value)}
              placeholder={suggestedName || 'Your business name'}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
            />
            <p className="mt-1 text-xs text-gray-500">This is how clients will see you</p>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Creating profile...' : 'Complete Setup'}
          </button>

          <button
            type="button"
            onClick={() => {
              setShowGoogleProfileForm(false);
              setGoogleAccessToken('');
            }}
            className="w-full text-gray-600 hover:text-gray-800 text-sm py-2"
          >
            Cancel
          </button>
        </form>
      </AuthLayout>
    );
  }

  // Success state
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex flex-col p-4">
        <div className="w-full max-w-md mx-auto flex flex-col flex-1">
          <div className="flex justify-center pt-4">
            <img src={logo} alt="BookDay" className="h-12 w-auto" />
          </div>

          <div className="flex-1" />

          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">You're almost there!</h2>
            <p className="text-gray-600 mb-2">We've sent a confirmation link to</p>
            <p className="text-pink-600 font-medium mb-6">{registeredEmail}</p>

            <div className="bg-pink-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-pink-800">
                Click the link in the email to activate your expert account and start managing your bookings.
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100">
              <Link to="/expert/login" className="text-gray-600 hover:text-pink-600 text-sm">
                Already confirmed? <span className="font-medium">Sign in</span>
              </Link>
            </div>
          </div>

          <div className="flex-1" />

          <div className="text-center pb-4 text-sm text-gray-500">
            <a href="/terms" className="text-pink-600 hover:underline">Terms of Service</a>
            {' · '}
            <a href="/privacy" className="text-pink-600 hover:underline">Privacy Policy</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthLayout role="expert" title="Sign up as Expert">
      {/* Progress indicator */}
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center space-x-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            step === 'phone' ? 'bg-pink-600 text-white' : 'bg-pink-100 text-pink-600'
          }`}>
            {step !== 'phone' ? '✓' : '1'}
          </div>
          <div className={`w-12 h-1 ${step !== 'phone' ? 'bg-pink-600' : 'bg-gray-200'}`} />
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            step === 'verify' ? 'bg-pink-600 text-white' : step === 'account' ? 'bg-pink-100 text-pink-600' : 'bg-gray-200 text-gray-400'
          }`}>
            {step === 'account' ? '✓' : '2'}
          </div>
          <div className={`w-12 h-1 ${step === 'account' ? 'bg-pink-600' : 'bg-gray-200'}`} />
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            step === 'account' ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-400'
          }`}>
            3
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div role="alert" className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm mb-5" aria-live="polite">
          {error}
        </div>
      )}

      {/* Step 1: Phone */}
      {step === 'phone' && (
        <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-5">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              {...phoneForm.register('phone')}
              id="phone"
              type="tel"
              autoComplete="tel"
              placeholder="+1 234 567 8900"
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors ${
                phoneForm.formState.errors.phone ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {phoneForm.formState.errors.phone && (
              <p className="mt-1 text-sm text-red-600">{phoneForm.formState.errors.phone.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">We'll send you a verification code</p>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Sending code...' : 'Send verification code'}
          </button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          {/* Google Sign Up */}
          <GoogleSignInButton
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            disabled={isLoading}
          />

          <div className="text-center text-sm">
            Already have an account?{' '}
            <Link to="/expert/login" className="text-pink-600 hover:underline font-medium">Log in</Link>
          </div>
        </form>
      )}

      {/* Step 2: Verify Code */}
      {step === 'verify' && (
        <form onSubmit={codeForm.handleSubmit(onCodeSubmit)} className="space-y-5">
          <div className="text-center mb-4">
            <p className="text-gray-600">Enter the code sent to</p>
            <p className="font-medium text-pink-600">{phone}</p>
          </div>

          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              {...codeForm.register('code')}
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              maxLength={8}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors text-center text-2xl tracking-widest ${
                codeForm.formState.errors.code ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {codeForm.formState.errors.code && (
              <p className="mt-1 text-sm text-red-600">{codeForm.formState.errors.code.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Verifying...' : 'Verify code'}
          </button>

          <div className="text-center space-y-2">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || isLoading}
              className="text-pink-600 hover:underline text-sm disabled:text-gray-400 disabled:no-underline"
            >
              {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
            </button>
            <br />
            <button
              type="button"
              onClick={() => { setStep('phone'); setError(''); }}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Use a different number
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Account Details */}
      {step === 'account' && (
        <form onSubmit={accountForm.handleSubmit(onAccountSubmit)} className="space-y-5">
          <div className="bg-green-50 rounded-lg p-3 mb-4">
            <p className="text-sm text-green-800 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Phone verified: {phone}
            </p>
          </div>

          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
            <input
              {...accountForm.register('displayName')}
              id="displayName"
              type="text"
              autoComplete="name"
              placeholder="Your business or professional name"
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors ${
                accountForm.formState.errors.displayName ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {accountForm.formState.errors.displayName && (
              <p className="mt-1 text-sm text-red-600">{accountForm.formState.errors.displayName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              {...accountForm.register('email')}
              id="email"
              type="email"
              autoComplete="email"
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors ${
                accountForm.formState.errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {accountForm.formState.errors.email && (
              <p className="mt-1 text-sm text-red-600">{accountForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <select
              {...accountForm.register('timezone')}
              id="timezone"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time (US)</option>
              <option value="America/Chicago">Central Time (US)</option>
              <option value="America/Denver">Mountain Time (US)</option>
              <option value="America/Los_Angeles">Pacific Time (US)</option>
              <option value="Europe/London">London</option>
              <option value="Europe/Paris">Paris</option>
              <option value="Europe/Berlin">Berlin</option>
              <option value="Asia/Tokyo">Tokyo</option>
              <option value="Australia/Sydney">Sydney</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Your local timezone for scheduling</p>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              {...accountForm.register('password')}
              id="password"
              type="password"
              autoComplete="new-password"
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors ${
                accountForm.formState.errors.password ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {accountForm.formState.errors.password && (
              <p className="mt-1 text-sm text-red-600">{accountForm.formState.errors.password.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">At least 10 characters</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
            <input
              {...accountForm.register('confirmPassword')}
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors ${
                accountForm.formState.errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {accountForm.formState.errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{accountForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ExpertSignup;
